"use client";

import React, { type ReactElement, useEffect, useMemo } from "react";
import { useI18next } from "gatsby-plugin-react-i18next";

import AlertModal from "@components/alert_modal";
import { useLocation } from "@gatsbyjs/reach-router";
import { useConfigurationState } from "@stores/configuration";
import { defaultRipple } from "@utilities/helper";
import {
  getLocalRestrictions,
  popupContentOf,
  type RestrictionProduct,
} from "@utilities/restrictions";

/** The product a page sells, for the popup's wording; eSIM elsewhere.
 *  The path may carry a language prefix (/de/sim-cards/). */
const productOf = (pathname: string): RestrictionProduct | undefined => {
  if (/(^|\/)sim-cards(\/|$)/.test(pathname)) return "simcard";
  if (/(^|\/)bne-go-portable-esim-wifi-router(\/|$)/.test(pathname)) {
    return "bne_go";
  }
  if (/(^|\/)bne-guard-vpn(\/|$)/.test(pathname)) return "vpn";
  return undefined;
};

const RestrictedToPurchasePopup = (): ReactElement => {
  const { t, language } = useI18next();
  const configurationState = useConfigurationState();

  const countryCode = configurationState.countryCodeFromApi.toUpperCase();
  const restrictions = useMemo(
    () => getLocalRestrictions(countryCode),
    [countryCode],
  );

  // The record carries the wording per product (pts: pop_up_text_<lang> for
  // eSIM, pop_up_simcard_text_<lang> for SIM cards, …), so the SIM cards
  // shop no longer reads "new eSIM purchases are not available".
  const { pathname } = useLocation();
  const product = productOf(pathname);
  const popupContent = useMemo(
    () => popupContentOf(restrictions, language, product),
    [restrictions, language, product],
  );

  useEffect(() => {
    configurationState.setIsNeededToShowRestrictedToPurchasePopup(
      restrictions?.show_pop_up ?? false,
    );
  }, [restrictions]);

  return (
    <AlertModal
      type="warning"
      isModalOpen={
        configurationState.isNeededToShowRestrictedToPurchasePopup &&
        configurationState.showRestrictedToPurchasePopup
      }
      setIsModalOpen={(value: boolean) => {
        configurationState.setShowRestrictedToPurchasePopup(value);
      }}
    >
      <div className="flex flex-col gap-4">
        <div className="text-center font-bold">{popupContent?.title}</div>
        <div className="whitespace-pre-wrap text-start">
          {popupContent?.text}
        </div>
        <button
          ref={defaultRipple}
          className="custom-btn-primary w-full"
          onClick={() => {
            configurationState.setShowRestrictedToPurchasePopup(false);
          }}
        >
          {t("got_it")}
        </button>
      </div>
    </AlertModal>
  );
};

export default RestrictedToPurchasePopup;
